/**
 * Adaptador real para Google Sheets API v4 (spec/02-architecture.md §3, §4 F3).
 *
 * Cumple estrictamente los requisitos de integridad:
 *  - Append-only estricto (INV-6): solo utiliza `spreadsheets.values.append`
 *    para asientos; jamas ejecuta update ni batchUpdate sobre filas de entries.
 *  - Lanza RateLimitError ante HTTP 429 para activar retroceso exponencial
 *    (FR-STORE-9, withExponentialBackoff).
 *  - Formato de fila identico al contrato canónico (spec/03-data-model.md §4).
 */

import {
  RateLimitError,
  type SheetRow,
  type SheetsAdapter,
} from "./sheets-adapter.js";
import type { SummaryRow, SummarySheetWriter } from "./sheets-export.js";

export const ENTRIES_HEADERS: readonly string[] = [
  "entry_id",
  "device_id",
  "device_seq",
  "date",
  "description",
  "origin",
  "reverses_entry_id",
  "created_at",
  "postings",
  "entry_hash",
  "device_chain_hash",
];

export interface GoogleSheetsAdapterOptions {
  readonly spreadsheetId: string;
  readonly getAccessToken: () => Promise<string | undefined>;
  readonly fetchImpl?: typeof fetch;
}

export class GoogleSheetsAdapter implements SheetsAdapter {
  private readonly spreadsheetId: string;
  private readonly getAccessToken: () => Promise<string | undefined>;
  private readonly fetchImpl: typeof fetch;

  constructor(options: GoogleSheetsAdapterOptions) {
    this.spreadsheetId = options.spreadsheetId;
    this.getAccessToken = options.getAccessToken;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    const token = await this.getAccessToken();
    if (!token) {
      throw new Error("No hay sesion activa con Google: token de acceso no disponible");
    }
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  async listExistingEntryIds(): Promise<Set<string>> {
    const headers = await this.getAuthHeaders();
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${encodeURIComponent(
      this.spreadsheetId,
    )}/values/entries!A2:A`;

    const res = await this.fetchImpl(url, { method: "GET", headers });
    if (res.status === 429) throw new RateLimitError();
    if (!res.ok) {
      if (res.status === 404) return new Set();
      throw new Error(`Fallo al consultar entradas existentes en Sheets (HTTP ${res.status})`);
    }

    const data = (await res.json()) as { values?: string[][] };
    const ids = new Set<string>();
    if (Array.isArray(data.values)) {
      for (const row of data.values) {
        if (row[0] && typeof row[0] === "string" && row[0].trim().length > 0) {
          ids.add(row[0].trim());
        }
      }
    }
    return ids;
  }

  async appendEntries(rows: readonly SheetRow[]): Promise<void> {
    if (rows.length === 0) return;
    const headers = await this.getAuthHeaders();
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${encodeURIComponent(
      this.spreadsheetId,
    )}/values/entries!A:K:append?valueInputOption=RAW&insertDataOption=INSERT_ROWS`;

    const rawValues = rows.map((r) => [
      r.entryId,
      r.deviceId,
      r.deviceSeq,
      r.date,
      r.description,
      r.origin,
      r.reversesEntryId ?? "",
      r.createdAt,
      r.postingsJson,
      r.entryHash,
      r.deviceChainHash,
    ]);

    const res = await this.fetchImpl(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ values: rawValues }),
    });

    if (res.status === 429) throw new RateLimitError();
    if (!res.ok) {
      throw new Error(`Fallo al anexar asientos a Sheets (HTTP ${res.status})`);
    }
  }

  async readAllEntries(): Promise<SheetRow[]> {
    const headers = await this.getAuthHeaders();
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${encodeURIComponent(
      this.spreadsheetId,
    )}/values/entries!A2:K`;

    const res = await this.fetchImpl(url, { method: "GET", headers });
    if (res.status === 429) throw new RateLimitError();
    if (!res.ok) {
      if (res.status === 404) return [];
      throw new Error(`Fallo al leer asientos de Sheets (HTTP ${res.status})`);
    }

    const data = (await res.json()) as { values?: unknown[][] };
    if (!Array.isArray(data.values)) return [];

    const sheetRows: SheetRow[] = [];
    for (const row of data.values) {
      if (!Array.isArray(row) || row.length < 11) continue;
      sheetRows.push({
        entryId: String(row[0] ?? ""),
        deviceId: String(row[1] ?? ""),
        deviceSeq: Number(row[2] ?? 0),
        date: String(row[3] ?? ""),
        description: String(row[4] ?? ""),
        origin: String(row[5] ?? ""),
        reversesEntryId: row[6] ? String(row[6]) : null,
        createdAt: String(row[7] ?? ""),
        postingsJson: String(row[8] ?? "[]"),
        entryHash: String(row[9] ?? ""),
        deviceChainHash: String(row[10] ?? ""),
      });
    }

    return sheetRows;
  }
}

/**
 * Crea una hoja de cálculo nueva en Google Drive con las 4 pestañas requeridas
 * (spec/03-data-model.md §4: entries, accounts, budgets_goals, meta).
 */
export async function createFinVozSpreadsheet(
  accessToken: string,
  title = "FinVoz — Finanzas Personales",
  fetchImpl: typeof fetch = globalThis.fetch.bind(globalThis),
): Promise<string> {
  const res = await fetchImpl("https://sheets.googleapis.com/v4/spreadsheets", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      properties: { title },
      sheets: [
        { properties: { title: "entries" } },
        { properties: { title: "accounts" } },
        { properties: { title: "budgets_goals" } },
        { properties: { title: "meta" } },
        { properties: { title: "Resumen actual" } },
      ],
    }),
  });

  if (!res.ok) {
    throw new Error(`Fallo al crear la hoja de calculo en Google Drive (HTTP ${res.status})`);
  }

  const data = (await res.json()) as { spreadsheetId: string };

  // Inicializar encabezados de las pestañas
  await fetchImpl(
    `https://sheets.googleapis.com/v4/spreadsheets/${data.spreadsheetId}/values:batchUpdate`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        valueInputOption: "RAW",
        data: [
          {
            range: "entries!A1:K1",
            values: [ENTRIES_HEADERS],
          },
          {
            range: "accounts!A1:G1",
            values: [["op_id", "target_kind", "target_id", "field", "value", "updated_at", "device_id"]],
          },
          {
            range: "budgets_goals!A1:G1",
            values: [["op_id", "target_kind", "target_id", "field", "value", "updated_at", "device_id"]],
          },
          {
            range: "meta!A1:D1",
            values: [["device_id", "last_seq_observed", "last_chain_hash_observed", "observed_at"]],
          },
          {
            range: "'Resumen actual'!A1:G1",
            values: [SUMMARY_HEADERS],
          },
        ],
      }),
    },
  );

  // Crear o localizar carpeta 'FinVoz' en Google Drive y vincular la hoja dentro (Scope drive.file)
  // Fail-closed: si la carpeta o la vinculación fallan, la operación lanza un error explícito.
  const folderId = await ensureFinVozFolder(accessToken, fetchImpl);
  const moveRes = await fetchImpl(
    `https://www.googleapis.com/drive/v3/files/${data.spreadsheetId}?addParents=${encodeURIComponent(folderId)}&fields=id,parents`,
    {
      method: "PATCH",
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );

  if (!moveRes.ok) {
    throw new Error(`Fallo al vincular la hoja a la carpeta 'FinVoz' en Google Drive (HTTP ${moveRes.status})`);
  }

  return data.spreadsheetId;
}

/**
 * Localiza o crea la carpeta 'FinVoz' en Google Drive dentro del scope mínimo drive.file.
 * Fail-closed: cualquier fallo de red, estado HTTP no exitoso o respuesta incompleta lanza Error.
 */
export async function ensureFinVozFolder(
  accessToken: string,
  fetchImpl: typeof fetch = globalThis.fetch.bind(globalThis),
): Promise<string> {
  const q = encodeURIComponent("name = 'FinVoz' and mimeType = 'application/vnd.google-apps.folder' and trashed = false");
  const searchRes = await fetchImpl(`https://www.googleapis.com/drive/v3/files?q=${q}&fields=files(id,name)`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!searchRes.ok) {
    throw new Error(`Fallo al buscar la carpeta 'FinVoz' en Google Drive (HTTP ${searchRes.status})`);
  }

  const data = (await searchRes.json()) as { files?: Array<{ id: string }> };
  if (data.files && data.files.length > 0 && data.files[0]?.id) {
    return data.files[0].id;
  }

  const createRes = await fetchImpl("https://www.googleapis.com/drive/v3/files", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name: "FinVoz",
      mimeType: "application/vnd.google-apps.folder",
    }),
  });

  if (!createRes.ok) {
    throw new Error(`Fallo al crear la carpeta 'FinVoz' en Google Drive (HTTP ${createRes.status})`);
  }

  const folderData = (await createRes.json()) as { id?: string };
  if (!folderData.id) {
    throw new Error("Respuesta inválida de Google Drive al crear la carpeta 'FinVoz'");
  }

  return folderData.id;
}

export const SUMMARY_HEADERS: readonly string[] = [
  "account_id",
  "account_name",
  "account_type",
  "balance_minor",
  "currency",
  "scale",
  "as_of",
];

export interface GoogleSheetsSummaryWriterOptions {
  readonly spreadsheetId: string;
  readonly getAccessToken: () => Promise<string | undefined>;
  readonly fetchImpl?: typeof fetch;
  /** Nombre de la pestaña de resumen regenerable. No es el libro (no aplica INV-6). */
  readonly sheetTitle?: string;
}

/**
 * Escritor real de la pestaña "Resumen actual". A diferencia de `GoogleSheetsAdapter`
 * (append-only estructural sobre `entries`), usa `values.update` deliberadamente:
 * el resumen es una vista derivada y regenerable (INV-4), nunca el libro.
 */
export class GoogleSheetsSummaryWriter implements SummarySheetWriter {
  private readonly spreadsheetId: string;
  private readonly getAccessToken: () => Promise<string | undefined>;
  private readonly fetchImpl: typeof fetch;
  private readonly sheetTitle: string;

  constructor(options: GoogleSheetsSummaryWriterOptions) {
    this.spreadsheetId = options.spreadsheetId;
    this.getAccessToken = options.getAccessToken;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.sheetTitle = options.sheetTitle ?? "Resumen actual";
  }

  async replaceSummary(rows: readonly SummaryRow[]): Promise<void> {
    const token = await this.getAccessToken();
    if (!token) {
      throw new Error("No hay sesion activa con Google: token de acceso no disponible");
    }
    const headers: HeadersInit = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

    const values = [
      SUMMARY_HEADERS,
      ...rows.map((r) => [r.accountId, r.accountName, r.accountType, r.balanceMinor.toString(), r.currency, r.scale, r.asOf]),
    ];

    const range = `'${this.sheetTitle}'!A1:G${values.length}`;
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${encodeURIComponent(
      this.spreadsheetId,
    )}/values/${encodeURIComponent(range)}?valueInputOption=RAW`;

    const res = await this.fetchImpl(url, {
      method: "PUT",
      headers,
      body: JSON.stringify({ values }),
    });

    if (res.status === 429) throw new RateLimitError();
    if (!res.ok) {
      throw new Error(`Fallo al regenerar 'Resumen actual' en Sheets (HTTP ${res.status})`);
    }
  }
}
